import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@/lib/use-server-fn";
import { useQuery } from "@tanstack/react-query";
import { Loader2, FileText, Hammer, Newspaper, CalendarDays, Video, Images, Quote, Mail } from "lucide-react";
import { adminStats } from "@/lib/admin.functions";
import { adminStatsQuery } from "@/lib/admin-queries";
import { formatShortDate } from "@/lib/format";

export const Route = createFileRoute("/admin/")({
  component: AdminDashboard,
});

const CARDS = [
  { key: "proposals", label: "Propostas", icon: FileText, to: "/admin/propostas" },
  { key: "works", label: "Trabalhos", icon: Hammer, to: "/admin/trabalhos" },
  { key: "news", label: "Notícias", icon: Newspaper, to: "/admin/noticias" },
  { key: "events", label: "Eventos", icon: CalendarDays, to: "/admin/agenda" },
  { key: "videos", label: "Vídeos", icon: Video, to: "/admin/videos" },
  { key: "galleries", label: "Galerias", icon: Images, to: "/admin/galerias" },
  { key: "testimonials", label: "Depoimentos", icon: Quote, to: "/admin/depoimentos" },
  { key: "contacts_new", label: "Mensagens novas", icon: Mail, to: "/admin/mensagens" },
];

function AdminDashboard() {
  const callStats = useServerFn(adminStats);
  const { data, isLoading } = useQuery({ ...adminStatsQuery(), queryFn: () => callStats() });

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl text-foreground">Painel</h1>
        <p className="text-muted-foreground">Visão geral do conteúdo da campanha.</p>
      </div>

      {isLoading ? (
        <Loader2 className="size-6 animate-spin text-primary" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {CARDS.map((c) => {
              const Icon = c.icon;
              return (
                <Link
                  key={c.key}
                  to={c.to}
                  className="rounded-xl border border-border bg-card p-5 shadow-soft transition-transform hover:-translate-y-0.5"
                >
                  <Icon className="mb-3 size-5 text-primary" />
                  <p className="font-display text-3xl text-foreground">{data?.counts[c.key] ?? 0}</p>
                  <p className="text-sm text-muted-foreground">{c.label}</p>
                </Link>
              );
            })}
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-xl border border-border bg-card p-5">
              <h2 className="mb-3 font-display text-lg text-foreground">Contatos recentes</h2>
              <div className="space-y-3">
                {(data?.recentContacts ?? []).length === 0 && (
                  <p className="text-sm text-muted-foreground">Nenhum contato recente.</p>
                )}
                {data?.recentContacts.map((c: any) => (
                  <div key={c.id} className="border-b border-border pb-2 last:border-0">
                    <p className="text-sm font-medium text-foreground">{c.name}</p>
                    <p className="truncate text-xs text-muted-foreground">{c.message}</p>
                    <p className="text-[11px] text-muted-foreground">{formatShortDate(c.created_at)}</p>
                  </div>
                ))}
              </div>
              <Link to="/admin/mensagens" className="mt-3 inline-block text-sm font-medium text-primary underline">
                Ver todas
              </Link>
            </div>

            <div className="rounded-xl border border-border bg-card p-5">
              <h2 className="mb-3 font-display text-lg text-foreground">Quero participar</h2>
              <div className="space-y-3">
                {(data?.recentParticipation ?? []).length === 0 && (
                  <p className="text-sm text-muted-foreground">Nenhum registro recente.</p>
                )}
                {data?.recentParticipation.map((c: any) => (
                  <div key={c.id} className="border-b border-border pb-2 last:border-0">
                    <p className="text-sm font-medium text-foreground">{c.name} — {c.interest}</p>
                    <p className="text-[11px] text-muted-foreground">{formatShortDate(c.created_at)}</p>
                  </div>
                ))}
              </div>
              <Link to="/admin/mensagens" className="mt-3 inline-block text-sm font-medium text-primary underline">
                Ver todas
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
